import { Link } from '@reach/router';
import React, { useContext, useState } from 'react';
import { Query } from 'react-apollo';
import slugify from 'slugify';
import styled from 'styled-components';
import NewAccountModal from '../components/NewAccountModal';
import { ButtonIconRound, Icon, ScrollView } from '../components/ui';
import { Loading, variables } from '../components/utils';
import ErrorScreen from '../components/utils/ErrorScreen';
import { SELECTED_ACCOUNT_ID, AccountContext } from '../context/account';
import { SELECTED_NOTEBOOK_ID } from '../context/NotebookContext';
import { UserContext } from '../context/UserContext';
import redirect from '../lib/redirect';
import { accountsQuery } from '../queries/account';
import { notebooksQuery } from '../queries/notebooks';
import { AccountsQuery } from '../queries/__generated__/AccountsQuery';
import { NotebooksQuery, NotebooksQueryVariables } from '../queries/__generated__/NotebooksQuery';
import logo from '../images/mark-blue.svg';

interface NotebookButtonProps {
  notebook: {
    id: string;
    name: string;
  };
  account: {
    id: string;
  };
}

const OutlineButton = styled.button.attrs({
  className: 'flex br2 ba hover-navy pv2 ph3 w-100 pointer'
})`
  border-color: ${variables.lightGray};

  &:hover {
    border-color: ${variables.navy};
  }
`;

const NotebookButton: React.FC<NotebookButtonProps> = ({ notebook, account }) => {
  const { selectAccount } = useContext(AccountContext);

  return (
    <OutlineButton
      onClick={() => {
        localStorage.setItem(SELECTED_ACCOUNT_ID, account.id);
        selectAccount(account.id);
        localStorage.setItem(SELECTED_NOTEBOOK_ID, notebook.id);
        redirect('/notebook');
      }}
      data-testid={`notebook-${slugify(notebook.name)}`}
    >
      <p className="f6 mv2" data-testid="notebook-name">
        {notebook.name}
      </p>
    </OutlineButton>
  );
};

const HomePage = () => {
  const [showNewAccountModal, setShowNewAccountModal] = useState(false);

  return (
    <Query<AccountsQuery> query={accountsQuery}>
      {({ loading, data, error }) => {
        if (loading) {
          return <Loading />;
        }
        if (error) {
          return <ErrorScreen error={error.message} />;
        }

        const accounts = data!.accounts! || [];

        return (
          <div className="fixed absolute--fill flex">
            <ScrollView className="vh-100 pa3 pa4-ns">
              <div className="tc pb2">
                <img className="w2 mb3" src={logo} alt="Quala" />
                <h2 className="f2 display mb0">Your Spaces</h2>
                <p className="f6 mb4">
                  Select a space or{' '}
                  <button
                    className="pointer blue hover-navy"
                    onClick={() => setShowNewAccountModal(true)}
                    data-testid={`new-account-button`}
                  >
                    create a new one
                  </button>
                  .
                </p>
              </div>

              {accounts.map((account, index) => (
                <Query<NotebooksQuery, NotebooksQueryVariables>
                  query={notebooksQuery}
                  variables={{ input: { accountId: account.id } }}
                  key={account.id}
                >
                  {({ loading, data }) => {
                    if (loading) {
                      return <Loading />;
                    }

                    const notebooks = data!.notebooks!;

                    if (!notebooks) {
                      return <ErrorScreen error="No Notebooks found" />;
                    }

                    return (
                      <div className="mw6 center" data-testid={`account-${index}`}>
                        <ul className="list pa0 ma0" data-testid="notebooks-list">
                          {notebooks.map(notebook => (
                            <li key={notebook.id} className="ph2 mb2">
                              <NotebookButton account={account} notebook={notebook} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }}
                </Query>
              ))}
            </ScrollView>
            {showNewAccountModal && <NewAccountModal setShowModal={setShowNewAccountModal} />}
          </div>
        );
      }}
    </Query>
  );
};

export default HomePage;
